
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, MapPin, Edit, Trash2, Navigation, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { LocationInput } from '@/components/LocationInput';
import { DeleteLocationDialog } from './DeleteLocationDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useFavoriteLocations, FavoriteLocation } from '@/hooks/useFavoriteLocations';
import { lookupBortle, lookupBortleBatch } from '@/utils/bortleLookup';
import { BortleBadge } from '@/components/locations/BortleBadge';

type Location = FavoriteLocation;

interface FavoriteLocationsProps {
  onLocationSelect: (location: Location | null) => void;
  selectedLocation: Location | null;
}

export const FavoriteLocations: React.FC<FavoriteLocationsProps> = ({
  onLocationSelect,
  selectedLocation
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { locations, isLoading: loading, invalidate: loadLocations } = useFavoriteLocations();
  const [dynamicBortles, setDynamicBortles] = useState<Record<string, number>>({});

  // For locations without a stored bortle_class, look them up dynamically and
  // persist the result back to the DB so subsequent visits don't need to
  // re-query PMTiles (and aren't at the mercy of transient lookup failures).
  useEffect(() => {
    const missing = locations.filter((l) => l.bortle_class == null);
    if (missing.length === 0) return;
    let cancelled = false;
    lookupBortleBatch(
      missing.map((l) => ({ key: l.id, lat: l.latitude, lng: l.longitude }))
    ).then(async (results) => {
      if (cancelled) return;
      const entries = Object.entries(results);
      if (entries.length === 0) return;
      setDynamicBortles((prev) => ({ ...prev, ...results }));

      // Fire-and-forget backfill. Errors are logged but never shown — this is
      // background self-healing, not a user-visible operation.
      await Promise.allSettled(
        entries.map(([id, bortle]) =>
          supabase
            .from('favorite_locations')
            .update({ bortle_class: bortle })
            .eq('id', id)
            .is('bortle_class', null)
            .then(({ error }) => {
              if (error) console.error('[FavoriteLocations] bortle backfill failed', id, error);
            })
        )
      );

      // Refresh the query so cards show the DB value on the next render —
      // keeps state single-sourced (DB), avoids drift between dynamicBortles
      // and favorite_locations row.
      if (!cancelled) loadLocations();
    });
    return () => {
      cancelled = true;
    };
  }, [locations, loadLocations]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    location: Location | null;
    usedInTrips: number;
  }>({ isOpen: false, location: null, usedInTrips: 0 });
  const [formData, setFormData] = useState({
    favoriteName: '',
    notes: ''
  });
  const [selectedLocationData, setSelectedLocationData] = useState<{
    name: string;
    latitude: number;
    longitude: number;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLocationData) {
      toast({
        title: 'Location Required',
        description: 'Please select a location first',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.favoriteName.trim()) {
      toast({
        title: 'Favorite Name Required',
        description: 'Please enter a name for this favorite location',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingLocation) {
        const { error } = await supabase
          .from('favorite_locations')
          .update({
            name: selectedLocationData.name, // Store the geocoded location name
            display_name: formData.favoriteName, // Store the user's custom name
            latitude: selectedLocationData.latitude,
            longitude: selectedLocationData.longitude,
            notes: formData.notes || null
          })
          .eq('id', editingLocation.id);

        if (error) throw error;
        toast({
          title: 'Success',
          description: 'Location updated successfully',
        });
      } else {
        // Insert immediately — don't block the add on the Bortle tile lookup (it hits a
        // remote PMTiles server that can be slow or down). The Bortle class fills in after.
        const { data: inserted, error } = await supabase
          .from('favorite_locations')
          .insert({
            user_id: user?.id,
            name: selectedLocationData.name, // Store the geocoded location name
            display_name: formData.favoriteName, // Store the user's custom name
            latitude: selectedLocationData.latitude,
            longitude: selectedLocationData.longitude,
            notes: formData.notes || null,
            bortle_class: null,
          })
          .select('id')
          .single();

        if (error) throw error;
        toast({
          title: 'Success',
          description: 'Location added successfully',
        });

        // Resolve the Bortle class in the background and patch the row when it lands,
        // so adding a location stays instant even when the tile server is slow.
        const newId = inserted?.id;
        const { latitude, longitude } = selectedLocationData;
        if (newId) {
          void (async () => {
            try {
              const bortle = await lookupBortle(latitude, longitude);
              if (bortle == null) return;
              await supabase
                .from('favorite_locations')
                .update({ bortle_class: bortle })
                .eq('id', newId);
              loadLocations();
            } catch {
              /* tile lookup or patch failed; bortle stays null */
            }
          })();
        }
      }

      setFormData({ favoriteName: '', notes: '' });
      setSelectedLocationData(null);
      setIsAddDialogOpen(false);
      setEditingLocation(null);
      loadLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast({
        title: 'Error',
        description: 'Failed to save location',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      favoriteName: location.display_name || location.name,
      notes: location.notes || ''
    });
    setSelectedLocationData({
      name: `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`,
      latitude: location.latitude,
      longitude: location.longitude
    });
    setIsAddDialogOpen(true);
  };

  const handleDeleteClick = async (location: Location) => {
    // Check if this location is used in any trip stops
    let tripCount = 0;
    if (user) {
      const { count } = await supabase
        .from('travel_stops')
        .select('id, travel_itineraries!inner(user_id)', { count: 'exact', head: true })
        .eq('travel_itineraries.user_id', user.id)
        .eq('latitude', location.latitude)
        .eq('longitude', location.longitude);
      tripCount = count || 0;
    }
    setDeleteDialog({ isOpen: true, location, usedInTrips: tripCount });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.location) return;

    try {
      const { error } = await supabase
        .from('favorite_locations')
        .delete()
        .eq('id', deleteDialog.location.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Location deleted successfully',
      });
      
      // Clear selection if deleted location was selected
      if (selectedLocation?.id === deleteDialog.location.id) {
        onLocationSelect(null);
      }
      
      loadLocations();
      setDeleteDialog({ isOpen: false, location: null, usedInTrips: 0 });
    } catch (error) {
      console.error('Error deleting location:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete location',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({ favoriteName: '', notes: '' });
    setSelectedLocationData(null);
    setEditingLocation(null);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'}`}>
          <div>
            <div className="h-7 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-64 bg-muted animate-pulse rounded mt-2" />
          </div>
          <div className={`h-10 ${isMobile ? 'w-full' : 'w-32'} bg-muted animate-pulse rounded`} />
        </div>
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border border-border">
              <CardHeader className="pb-2">
                <div className="h-5 w-3/4 bg-muted animate-pulse rounded" />
                <div className="h-4 w-1/2 bg-muted animate-pulse rounded mt-1" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 w-2/3 bg-muted animate-pulse rounded" />
                  <div className="h-4 w-1/2 bg-muted animate-pulse rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'}`}>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Your Locations</h2>
          <p className="text-muted-foreground">Select a location to start planning</p>
        </div>
        <Dialog 
          open={isAddDialogOpen} 
          onOpenChange={(open) => {
            setIsAddDialogOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className={`shadow-md ${isMobile ? 'w-full' : ''}`}>
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle>
                {editingLocation ? 'Edit Favorite Location' : 'Add New Favorite Location'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="favoriteName">Favorite Name</Label>
                <Input
                  id="favoriteName"
                  value={formData.favoriteName}
                  onChange={(e) => setFormData(prev => ({ ...prev, favoriteName: e.target.value }))}
                  placeholder="e.g., My favorite Milky Way spot"
                  required
                />
              </div>
              
              <div>
                <LocationInput onLocationChange={setSelectedLocationData} />
                {selectedLocationData && (
                  <div className="mt-2 p-3 bg-muted rounded-md text-sm">
                    <div className="font-medium text-foreground">{selectedLocationData.name}</div>
                    <div className="text-muted-foreground text-xs mt-1">
                      {selectedLocationData.latitude.toFixed(4)}, {selectedLocationData.longitude.toFixed(4)}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add notes about this location, parking info, best viewing spots, etc."
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingLocation ? 'Update' : 'Add'} Location
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {locations.length === 0 ? (
        <Card className="border border-border bg-muted/50">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <MapPin className="w-12 h-12 text-gold mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-slate-700">No locations yet</h3>
            <p className="text-slate-500 text-center mb-4">
              Add your favorite astrophotography locations to get started
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {locations.map((location) => (
            <Card 
              key={location.id} 
              className={`cursor-pointer transition-all duration-300 border ${
                selectedLocation?.id === location.id
                  ? 'ring-2 ring-gold bg-amber-50 border-gold shadow-md'
                  : 'border-border hover:bg-muted hover:border-gold/50 hover:shadow-md'
              }`}
              onClick={() => onLocationSelect({
                ...location,
                bortle_class: location.bortle_class ?? dynamicBortles[location.id] ?? null,
              })}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className={`text-base text-slate-800 ${isMobile ? 'text-sm' : ''} font-semibold`}>
                      {location.display_name || location.name}
                    </CardTitle>
                    <div className={`text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'} mt-1`}>
                      {location.name}
                    </div>
                  </div>
                  {selectedLocation?.id === location.id && (
                    <Badge className="bg-gold text-white text-xs">Selected</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Navigation className="w-3 h-3 text-gold flex-shrink-0" />
                      <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-mono`}>
                        {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                      </span>
                    </div>
                    {(() => {
                      const b = location.bortle_class ?? dynamicBortles[location.id];
                      return b != null ? <BortleBadge bortle={b} /> : null;
                    })()}
                  </div>
                  {location.notes && (
                    <div className="flex items-start gap-1">
                      <FileText className="w-3 h-3 mt-0.5 flex-shrink-0 text-gold" />
                      <p className={`${isMobile ? 'text-xs' : 'text-xs'} line-clamp-2`}>{location.notes}</p>
                    </div>
                  )}
                  <div className={`flex gap-2 pt-2 ${isMobile ? 'justify-center' : ''}`}>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-gold text-gold hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(location);
                      }}
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(location);
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DeleteLocationDialog
        isOpen={deleteDialog.isOpen}
        onOpenChange={(open) => setDeleteDialog({ isOpen: open, location: null, usedInTrips: 0 })}
        onConfirm={handleDeleteConfirm}
        locationName={deleteDialog.location?.display_name || deleteDialog.location?.name || ''}
        usedInTrips={deleteDialog.usedInTrips}
      />
    </div>
  );
};
